import React, { ChangeEvent, memo } from 'react';

import messages from './Messages';
import Collapsible from '../../../components/Collapsible';
import { TemplateFieldRow } from 'components/TemplateFields/styles';
import Input from 'components/Input';
import Button from 'components/Button';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import notify from 'components/Notify';
import { HiddenFieldValue, IField, ITask } from 'store/tasks/tasksType';
import {
  ICompany,
  ICompanyMember,
  IProjectTemplate,
} from 'store/companies/companiesTypes';
import { IUsers } from 'store/store';
import cloneDeep from 'lodash/cloneDeep';
import i18n, { languageDirection } from '../../../i18n';

interface IComponentProps {
  template: ITask;
  onSelectTemplate: (templateId: number, selected: boolean) => void;
  onUpdateTemplate: (template: IProjectTemplate) => Promise<void>;
  current?: any;
  autoSaveFieldChange?: boolean;
  company: ICompany;
  users: IUsers;
}

interface IComponentState {
  fields: { [key: string]: string };
  fieldOptions: { [key: string]: string[] };
  assignee: Array<number>;
  follower: Array<number>;
  verifier: number | undefined;
  taskPublisher: number | undefined;
  subTaskPublisher: number | undefined;
  loading: boolean;
}

type PersonTypes = 'verifier' | 'taskPublisher' | 'subTaskPublisher';
type MultiPersonTypes = 'assignee' | 'follower';

class ProjectTemplateView extends React.Component<
  IComponentProps,
  IComponentState
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      ...this.storePropToState(this.props.current, false),
      loading: false,
    };
  }
  componentDidUpdate(prevProps: Readonly<IComponentProps>): void {
    if (prevProps.current !== this.props.current) {
      this.setState(
        this.storePropToState(
          this.props.current,
          this.props.template.id === prevProps.template.id &&
            this.props.current === prevProps.current
        )
      );
    }
  }

  handleChangePerson = (type: PersonTypes) => (values: IOption[]) => {
    const state: {
      [key in PersonTypes]?: any;
    } = {};
    state[type] = values.length === 0 ? undefined : Number(values[0].value);
    this.setState(state as any, () => {
      if (this.props.autoSaveFieldChange) this.submitChanges();
    });
  };

  handleChangeMultiPerson = (type: MultiPersonTypes) => (values: IOption[]) => {
    const state: {
      [key in MultiPersonTypes]?: any;
    } = {};
    state[type] = values.map((v) => Number(v.value));
    this.setState(state as any, () => {
      if (this.props.autoSaveFieldChange) this.submitChanges();
    });
  };

  storePropToState(current: any, same: boolean): any {
    const c = current;
    if (c !== undefined)
      return {
        fields:
          this.state && this.state?.fields && same
            ? this.state.fields
            : c.overrides?.fields
              ? cloneDeep(c.overrides.fields)
              : {},
        fieldOptions:
          this.state && this.state?.fieldOptions && same
            ? this.state.fieldOptions
            : c.overrides?.fieldOptions
              ? cloneDeep(c.overrides.fieldOptions)
              : {},
        assignee: cloneDeep(c.overrides?.assigneeMemberIds),
        follower: cloneDeep(c.overrides?.followerMemberIds),
        verifier: cloneDeep(c.overrides?.verifierMemberId),
        taskPublisher: cloneDeep(c.overrides?.taskPublisherMemberId),
        subTaskPublisher: cloneDeep(c.overrides?.subTaskPublisherMemberId),
      };
    return {
      fields: {},
      fieldOptions: {},
      assignee: [],
      follower: [],
    };
  }

  handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onSelectTemplate(this.props.template.id, e.target.checked);
  };
  handleOnUpdateAllowRoot = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onUpdateTemplate({
      ...this.props.current!,
      allowRoot: e.target.checked,
    });
  };
  handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const current = this.state.fields;
    current[e.target.id] = e.target.value;
    this.setState({ fields: this.state.fields });
    if (this.props.autoSaveFieldChange) this.submitChanges();
  };
  handleSetShow = (e: ChangeEvent<HTMLInputElement>) => {
    const current = this.state.fields;
    current[e.target.name] = !e.target.checked ? HiddenFieldValue : '';
    this.setState({ fields: this.state.fields });
    if (this.props.autoSaveFieldChange) this.submitChanges();
  };

  onChangeFieldOptions = (fieldIndex: string) => (options: IOption[]) => {
    this.setState((prevState) => {
      const newOptions = options.reduce((list: string[], option) => {
        list.push(option.value.toString());
        return list;
      }, [] as string[]);
      return {
        fieldOptions: { ...prevState.fieldOptions, [fieldIndex]: newOptions },
      };
    });
    if (this.props.autoSaveFieldChange) this.submitChanges();
  };

  submitChanges = () => {
    const result: IProjectTemplate = cloneDeep(this.props.current!);
    if (!result.overrides) {
      result.overrides = {
        fields: {},
        fieldOptions: {},
        assigneeMemberIds: undefined,
        followerMemberIds: undefined,
        verifierMemberId: undefined,
        taskPublisherMemberId: undefined,
        subTaskPublisherMemberId: undefined,
      };
    }
    result.overrides.fields = this.state.fields;
    result.overrides.fieldOptions = this.state.fieldOptions;
    Object.keys(result.overrides.fieldOptions || {}).forEach((key) => {
      if (result.overrides.fieldOptions[key].length === 0)
        delete result.overrides.fieldOptions[key];
    });
    result.overrides.assigneeMemberIds = this.state.assignee;
    result.overrides.followerMemberIds = this.state.follower;
    result.overrides.verifierMemberId = this.state.verifier;
    result.overrides.taskPublisherMemberId = this.state.taskPublisher;
    result.overrides.subTaskPublisherMemberId = this.state.subTaskPublisher;
    this.setState({ loading: true });
    this.props.onUpdateTemplate(result).then(() => {
      this.setState({ loading: false });
      if (!this.props.autoSaveFieldChange)
        notify(i18n.t('saved', { ns: 'companyManagement' }) as string);
    });
  };

  render() {
    const { template, current, autoSaveFieldChange, company, users } =
      this.props;
    const {
      fields,
      verifier,
      taskPublisher,
      subTaskPublisher,
      fieldOptions,
      assignee,
      follower,
    } = this.state;
    const companyMembers: IOption[] = Object.values(company.members).reduce(
      (list: IOption[], companyMember: ICompanyMember) => {
        const item: IOption = {
          value: companyMember.userId,
          label: users.map![companyMember.userId]?.name || '',
        };
        if (!companyMember.inactive) list.push(item);
        return list;
      },
      []
    );
    Object.values(company.roles).forEach((role) => {
      const item: IOption = {
        value: -role.id,
        label: role.name,
      };
      companyMembers.push(item);
    }, []);
    const side = languageDirection() === 'rtl' ? 'right' : 'left';
    return (
      <div className="col s12 m12" style={{ float: side }}>
        <p className="col s12 m6" style={{ float: side }}>
          <Input
            type="checkbox"
            id={template.id!.toString()}
            checked={!!current}
            onChange={this.handleOnChange}
            disabled={template.archived}
            label={`${template.taskName} ${template.archived ? `(${i18n.t('archived', { ns: 'companyManagement' }) as string})` : ''}`}
          />
        </p>
        {current && (
          <>
            <p
              className="col s12 m6"
              style={{
                float: languageDirection() === 'rtl' ? 'right' : 'left',
              }}
            >
              <Input
                type="checkbox"
                id={template.id!.toString() + 'allowRoot'}
                checked={current.allowRoot}
                onChange={this.handleOnUpdateAllowRoot}
                label={
                  i18n.t('canCreateAsRoot', {
                    ns: 'companyManagement',
                  }) as string
                }
              />
            </p>
            <Collapsible
              header={
                i18n.t('customConfig', {
                  ns: 'companyManagement',
                }) as string
              }
              customBodyStyle={{ overflow: 'visible !important' }}
            >
              {template.fields.map((field: IField, key) => (
                <TemplateFieldRow
                  key={`field-${template.id}-${key}-${field.name}`}
                >
                  <div>
                    <div>{field.title}</div>
                  </div>
                  <div>
                    <Input
                      id={`show-field-${template.id}-${key}-${field.name}`}
                      name={field.name}
                      type="checkbox"
                      checked={fields[field.name] !== HiddenFieldValue}
                      onChange={this.handleSetShow}
                      label={
                        i18n.t('show', {
                          ns: 'companyManagement',
                        }) as string
                      }
                    />
                  </div>
                  {fields[field.name] !== HiddenFieldValue && (
                    <>
                      <div>
                        <Select
                          values={
                            fields[field.name]
                              ? [
                                  {
                                    value: fields[field.name],
                                    label: fields[field.name],
                                  },
                                ]
                              : []
                          }
                          onChangeValues={(options: IOption[]) => {
                            const value =
                              options.length > 0
                                ? options[0].value.toString()
                                : '';
                            const current = this.state.fields;
                            current[field.name] = value;
                            this.setState({ fields: current });
                            if (this.props.autoSaveFieldChange)
                              this.submitChanges();
                          }}
                          options={[
                            ...Object.values(field.options || {}).map(
                              (option) => ({ value: option, label: option })
                            ),
                            ...(fieldOptions[field.name] || []).map(
                              (option) => ({ value: option, label: option })
                            ),
                          ]}
                          label={
                            i18n.t('defaultValue', {
                              ns: 'companyManagement',
                            }) as string
                          }
                        />
                      </div>
                      <div>
                        <Select
                          values={
                            fieldOptions[field.name]
                              ? Object.values(fieldOptions[field.name]).reduce(
                                  (list: IOption[], value) => {
                                    const item: IOption = {
                                      value: value as string,
                                      label: value,
                                    };
                                    list.push(item);
                                    return list;
                                  },
                                  []
                                )
                              : []
                          }
                          options={[]}
                          onChangeValues={this.onChangeFieldOptions(field.name)}
                          letAddChip={true}
                          multiChoice={true}
                          label={
                            i18n.t('options', {
                              ns: 'companyManagement',
                            }) as string
                          }
                        />
                      </div>
                    </>
                  )}
                </TemplateFieldRow>
              ))}
              {template.haveVerifier && (
                <Select
                  values={
                    verifier
                      ? [
                          {
                            value: verifier!,
                            label:
                              verifier > 0
                                ? users.map[verifier].name!
                                : (company.roles[-verifier]?.displayName ??
                                  '?'),
                          },
                        ]
                      : []
                  }
                  options={companyMembers}
                  onChangeValues={this.handleChangePerson('verifier')}
                  placeholder={messages.defaultVerifier(template.verifierTitle)}
                  label={messages.defaultVerifier(template.verifierTitle)}
                />
              )}
              {template.haveSubTaskPublisher && (
                <Select
                  values={
                    subTaskPublisher
                      ? [
                          {
                            value: subTaskPublisher,
                            label:
                              subTaskPublisher > 0
                                ? users.map[subTaskPublisher].name
                                : (company.roles[-subTaskPublisher]
                                    ?.displayName ?? '?'),
                          },
                        ]
                      : []
                  }
                  options={companyMembers}
                  onChangeValues={this.handleChangePerson('subTaskPublisher')}
                  placeholder={messages.defaultSubTaskPublisher(
                    template.subTaskPublisherTitle
                  )}
                  label={messages.defaultSubTaskPublisher(
                    template.subTaskPublisherTitle
                  )}
                />
              )}
              {template.haveTaskPublisher && (
                <Select
                  values={
                    taskPublisher
                      ? [
                          {
                            value: taskPublisher,
                            label:
                              taskPublisher > 0
                                ? users.map[taskPublisher].name
                                : (company.roles[-taskPublisher]?.displayName ??
                                  '?'),
                          },
                        ]
                      : []
                  }
                  options={companyMembers}
                  onChangeValues={this.handleChangePerson('taskPublisher')}
                  placeholder={messages.defaultTaskPublisher(
                    template.taskPublisherTitle
                  )}
                  label={messages.defaultTaskPublisher(
                    template.taskPublisherTitle
                  )}
                />
              )}
              <Select
                values={
                  assignee?.length
                    ? assignee.map((id) => ({
                        value: id,
                        label:
                          id > 0
                            ? users.map[id]?.name || ''
                            : company.roles[-id]?.displayName || '?',
                      }))
                    : []
                }
                options={companyMembers}
                onChangeValues={this.handleChangeMultiPerson('assignee')}
                multiChoice={true}
                placeholder={
                  template.assigneeTitle
                    ? template.assigneeTitle
                    : (i18n.t('taskAssignee', { ns: 'taskManager' }) as string)
                }
                label={i18n.t('taskAssignee', { ns: 'taskManager' }) as string}
              />
              <Select
                values={
                  follower?.length
                    ? follower.map((id) => ({
                        value: id,
                        label:
                          id > 0
                            ? users.map[id]?.name || ''
                            : company.roles[-id]?.displayName || '?',
                      }))
                    : []
                }
                options={companyMembers}
                onChangeValues={this.handleChangeMultiPerson('follower')}
                multiChoice={true}
                placeholder={
                  template.followersTitle
                    ? template.followersTitle
                    : (i18n.t('taskFollower', { ns: 'taskManager' }) as string)
                }
                label={i18n.t('taskFollower', { ns: 'taskManager' }) as string}
              />
              {!autoSaveFieldChange && (
                <div style={{ padding: '10px' }}>
                  <Button
                    color="blue"
                    onClick={this.submitChanges}
                    loading={this.state.loading}
                  >
                    {i18n.t('save', { ns: 'companyManagement' }) as string}
                  </Button>
                </div>
              )}
            </Collapsible>
          </>
        )}
      </div>
    );
  }
}

export default memo(ProjectTemplateView);
